import {
  Fragment,
  SetStateAction,
  useEffect,
  useState,
  useRef,
  useCallback,
  memo,
} from "react";

import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Grid from "@mui/material/Grid";

import { captionsState, projectPickerState } from "./JotaiAtoms";

import { storyCaption } from "./storydat";

import {
  SendMessageBox,
  unfinishedMessageFromCache,
  NotifyMessage,
  getFilesFromDataTransfer,
  clearInputWithMathHighlights,
  syncInputsWithMathHighlights,
  drawMathHighlightsForMathInput,
} from "./SendMessageBox";
import { StoryItem } from "./StoryItem";
import { AutocompleteStory } from "./AutocompleteStory";
import { AutocompleteStoryInline } from "./AutocompleteStoryInline";
import { Thread } from "./Thread";
import store from "store2";

import * as u from "./utility";
import log from "./logger";
import { useAtomValue } from "jotai";
import { useHttpPost } from "./hooks";
import { MessagePreview } from "./MessagePreview";
import { TimelineChips, StoryChip } from "./TimeLineChips";
import { StyledTooltipContent, StyledTooltip } from "./themeConfig";
import { sortMessages } from "./SendMessageBox";
import { MessageItem, FileUpload } from "./MessageItem";

type CommentSectionProps = {
  onReload: () => void;
  handleSort: () => void;
  sortingOrder: boolean;
  setMessages: (value: React.SetStateAction<MessageItem[]>) => void;
  onMessageSent: (messsage?: NotifyMessage) => void;
  droppedFiles?: File[] | FileList;
  handleUploadedFileRemoved: () => void;
  item: StoryItem;
  bottomInputPresent: boolean;
  children: any;
  onSending?: () => void;
};

export function CommentSection({
  onReload,
  handleSort,
  sortingOrder,
  setMessages,
  onMessageSent,
  onSending,
  droppedFiles,
  handleUploadedFileRemoved,
  item,
  bottomInputPresent,
  children,
}: CommentSectionProps) {
  const [selectedFiles, setSelectedFiles] = useState<FileUpload[]>([]);
  const [message, setMessage] = useState<string>(
    unfinishedMessageFromCache(item)
  );
  const [password, setPassword] = useState<string>("");
  const [previousMessage, setPreviousMessage] = useState<string>("");
  const [focused, setFocused] = useState<number>(0);
  const [initialFocus, setInitialFocus] = useState<number>(1);
  const [bottomInputMounted, setBottomInputMounted] = useState<boolean>(false);
  const refFocus = useRef(0);
  const input1Ref = useRef<HTMLInputElement>();
  const input2Ref = useRef<HTMLInputElement>();
  const inputsFocusRef = useRef<{ visible1: boolean; visible2: boolean }>({
    visible1: false,
    visible2: false,
  });

  const handleMessageSent = (msg?: NotifyMessage) => {
    if (msg && msg.text) {
      setPreviousMessage(msg.text);
    }
    onMessageSent(msg);
  };

  const handleMessageSending = () => {
    if (input1Ref.current) {
      clearInputWithMathHighlights(input1Ref.current);
    }
    if (input2Ref.current) {
      clearInputWithMathHighlights(input2Ref.current);
    }

    if (onSending) {
      onSending();
    }
  };

  const handleFocusBlurEvents = (delta: number) => {
    refFocus.current += delta;
    setTimeout(() => {
      setFocused(refFocus.current);
    }, 400);
  };

  const handleFocus1 = () => {
    drawMathHighlightsForMathInput(input1Ref.current);
  };

  const handleFocus2 = () => {
    setInputFocused(true);
    drawMathHighlightsForMathInput(input2Ref.current);
  };

  const handleBlur1 = () => {
    handleFocusBlurEvents(-1);
    if (input1Ref.current) {
      syncInputsWithMathHighlights(input1Ref.current, input2Ref.current);
      drawMathHighlightsForMathInput(input2Ref.current);
    }
  };

  const handleBlur2 = () => {
    setInputFocused(false);
    handleFocusBlurEvents(-1);
    if (input2Ref.current) {
      syncInputsWithMathHighlights(input2Ref.current, input1Ref.current);
      drawMathHighlightsForMathInput(input1Ref.current);
    }
  };

  const handleInput1 = (value: string) => {
    // forcefully clear the both inputs after sending a message
    if (!value) {
      setMessage("");
    }
  };

  const handleInput2 = (value: string) => {
    // forcefully clear the both inputs after sending a message
    if (!value) {
      setMessage("");
    }
  };

  const handleVisible1 = (visible: boolean) => {
    const cur = inputsFocusRef.current;
    cur.visible1 = visible;

    if (!input1Ref.current) {
      return;
    }

    // re-focus
    if (
      visible &&
      !cur.visible2 &&
      document.activeElement == input2Ref.current
    ) {
      syncInputsWithMathHighlights(input2Ref.current, input1Ref.current);
      input1Ref.current.focus({ preventScroll: true });
    }
  };

  const handleVisible2 = (visible: boolean) => {
    const cur = inputsFocusRef.current;
    cur.visible2 = visible;

    if (!input1Ref.current || !input2Ref.current) {
      return;
    }

    // re-focus
    if (
      visible &&
      !cur.visible1 &&
      document.activeElement == input1Ref.current
    ) {
      syncInputsWithMathHighlights(input1Ref.current, input2Ref.current);
      input2Ref.current.focus({ preventScroll: true });
    }
  };

  useEffect(() => {
    if (focused > 0) {
      setInitialFocus(0);
    }
  }, [focused]);

  useEffect(() => {
    if (droppedFiles && !password) {
      const vis = inputsFocusRef.current;
      if (
        input1Ref.current &&
        vis.visible1 &&
        (!input2Ref.current || document.activeElement != input2Ref.current)
      ) {
        input1Ref.current.focus({ preventScroll: true });
      } else if (
        input2Ref.current &&
        vis.visible2 &&
        (!input1Ref.current || document.activeElement != input1Ref.current)
      ) {
        input2Ref.current.focus({ preventScroll: true });
      }
    }
  }, [droppedFiles]);

  // [1237] fix jitteriness in the ui when auto focusing on
  // the input whne there are multiple inputs in the threads
  // and stories a being loaded progressively
  useEffect(() => {
    if (bottomInputPresent) {
      setTimeout(() => {
        setBottomInputMounted(true);
      }, 3000);
    } else {
      setBottomInputMounted(false);
    }
  }, [bottomInputPresent]);

  useEffect(() => {
    // [focus on expand]
    // this initial focus without the time will be very jittery
    if (initialFocus > 0) {
      setTimeout(() => {
        if (initialFocus > 0) {
          input1Ref.current?.focus();
        }
      }, 150);
    }
  }, [initialFocus]);

  const theme = useTheme();
  const [inputFocused, setInputFocused] = useState(false);
  const [boxHovered, setBoxHovered] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const handleFocus = (e: any) => {
    setInputFocused(true);
    handleFocus1();
  };

  const handleBlur = (e: any) => {
    setInputFocused(false);
    handleBlur1();
  };

  const handleBoxMouseEnter = () => {
    setBoxHovered(true);
  };

  const handleBoxMouseLeave = () => {
    setBoxHovered(false);
  };

  const showButtons =
    boxHovered || inputFocused || message || selectedFiles.length > 0;

  useEffect(() => {
    if (boxRef.current) {
      const input = boxRef.current.querySelector(
        ".MuiInputBase-input"
      ) as HTMLElement;
      const buttons = boxRef.current.querySelector(
        ".MuiInputAdornment-root"
      ) as HTMLElement;
      const inputBase = boxRef.current.querySelector(
        ".MuiInputBase-root"
      ) as HTMLElement;

      // console.log(
      //   "Box:",
      //   boxRef.current.offsetHeight + "px, padding:",
      //   window.getComputedStyle(boxRef.current).padding
      // );
      // if (inputBase && input) {
      //   console.log(
      //     "Input: base@" +
      //       inputBase.offsetTop +
      //       " + input@" +
      //       input.offsetTop +
      //       " = " +
      //       (inputBase.offsetTop + input.offsetTop) +
      //       "px from top"
      //   );
      // }
      // if (inputBase && buttons) {
      //   console.log(
      //     "Buttons: base@" +
      //       inputBase.offsetTop +
      //       " + buttons@" +
      //       buttons.offsetTop +
      //       " = " +
      //       (inputBase.offsetTop + buttons.offsetTop) +
      //       "px from top"
      //   );
      // }
    }
  }, [inputFocused, message, selectedFiles]);

  return (
    <>
      <Box
        sx={{
          mb: 3,
          mt: 3,
          px: 2,
          maxWidth: "900px",
        }}
      >
        <Box
          ref={boxRef}
          onMouseEnter={handleBoxMouseEnter}
          onMouseLeave={handleBoxMouseLeave}
          sx={{
            px: 2,
            py: "7px",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.015)",
            borderRadius: "8px",
            display: "flex",
            alignItems: "flex-start",
            minHeight: "32px",
            "& .MuiInputBase-root": {
              fontSize: "14px",
              lineHeight: "20px",
              alignItems: "flex-start",
              marginTop: "2px",
              marginBottom: 0,
              marginLeft: 0,
              marginRight: 0,
            },
            "& .MuiInputBase-root::before": {
              display: "none",
            },
            "& .MuiInputBase-root::after": {
              display: "none",
            },
            "& .MuiInputLabel-root": {
              display: "none",
            },
            "& .MuiInputBase-input": {
              padding: "0",
              lineHeight: "20px",
              minHeight: "20px",
              marginTop: "0 !important",
              border: "none",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "var(--text-tertiary)",
              opacity: 1,
            },
            "& .MuiStack-root": {
              minHeight: "auto",
              width: "100%",
            },
            "& .MuiInputBase-input:not(:placeholder-shown) ~ .MuiInputAdornment-root":
              {
                display: "inline-flex !important",
              },
            "& .MuiInputAdornment-root": {
              display: showButtons ? "inline-flex" : "none",
              marginLeft: "0 !important",
              marginTop: "-4px !important",
              paddingTop: "0 !important",
              alignSelf: "flex-start !important",
              alignItems: "flex-start",
              lineHeight: "20px",
              height: "20px",
              "& .MuiIconButton-root": {
                display: "flex",
                marginTop: "0 !important",
              },
            },
          }}
        >
          <SendMessageBox
            onSending={handleMessageSending}
            item={item}
            setMessages={setMessages}
            id="top"
            onSent={handleMessageSent}
            droppedFiles={droppedFiles}
            onFileRemoved={handleUploadedFileRemoved}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            message={message}
            password={password}
            setPassword={setPassword}
            previousMessage={previousMessage}
            onBlur={handleBlur}
            onFocus={handleFocus}
            inputRef={input1Ref}
            onChange={handleInput1}
            onVisible={handleVisible1}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 2,
          px: 2,
          maxWidth: "900px",
        }}
      >
        <Stack direction="row" spacing={0.5}>
          <StyledTooltip title="Reload messages" placement="top">
            <IconButton
              size="small"
              onClick={onReload}
              sx={{
                padding: "4px",
                color: "var(--text-tertiary)",
                "&:hover": {
                  color: "var(--text-secondary)",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </StyledTooltip>
          <StyledTooltip
            title={sortingOrder ? "Newest first" : "Oldest first"}
            placement="top"
          >
            <IconButton
              size="small"
              onClick={handleSort}
              sx={{
                padding: "4px",
                color: "var(--text-tertiary)",
                "&:hover": {
                  color: "var(--text-secondary)",
                },
              }}
            >
              {sortingOrder ? (
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              ) : (
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </StyledTooltip>
        </Stack>
      </Box>

      {children}

      {bottomInputMounted && (
        <Box
          sx={{
            mb: 3,
            mt: 3,
            px: 2,
            maxWidth: "900px",
          }}
        >
          <Box
            onMouseEnter={handleBoxMouseEnter}
            onMouseLeave={handleBoxMouseLeave}
            sx={{
              px: 2,
              py: "7px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(0, 0, 0, 0.015)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "flex-start",
              minHeight: "32px",
              "& .MuiInputBase-root": {
                fontSize: "14px",
                lineHeight: "20px",
                alignItems: "flex-start",
                marginTop: "2px",
                marginBottom: 0,
                marginLeft: 0,
                marginRight: 0,
              },
              "& .MuiInputBase-root::before": {
                display: "none",
              },
              "& .MuiInputBase-root::after": {
                display: "none",
              },
              "& .MuiInputLabel-root": {
                display: "none",
              },
              "& .MuiInputBase-input": {
                padding: "0",
                lineHeight: "20px",
                minHeight: "20px",
                marginTop: "0 !important",
                border: "none",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "var(--text-tertiary)",
                opacity: 1,
              },
              "& .MuiStack-root": {
                minHeight: "auto",
                width: "100%",
              },
              "& .MuiInputBase-input:not(:placeholder-shown) ~ .MuiInputAdornment-root":
                {
                  display: "inline-flex !important",
                },
              "& .MuiInputAdornment-root": {
                display: showButtons ? "inline-flex" : "none",
                marginLeft: "0 !important",
                marginTop: "-4px !important",
                paddingTop: "0 !important",
                alignSelf: "flex-start !important",
                alignItems: "flex-start",
                lineHeight: "20px",
                height: "20px",
                "& .MuiIconButton-root": {
                  display: "flex",
                  marginTop: "0 !important",
                },
              },
            }}
          >
            <SendMessageBox
              onSending={handleMessageSending}
              item={item}
              setMessages={setMessages}
              id="bottom"
              onSent={handleMessageSent}
              droppedFiles={droppedFiles}
              onFileRemoved={handleUploadedFileRemoved}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              message={message}
              password={password}
              setPassword={setPassword}
              previousMessage={previousMessage}
              onBlur={handleBlur2}
              onFocus={handleFocus2}
              inputRef={input2Ref}
              onChange={handleInput2}
              onVisible={handleVisible2}
            />
          </Box>
        </Box>
      )}
    </>
  );
}

type StoryDetailsProps = {
  item: StoryItem;
  messages?: MessageItem[];
  setMessages: (value: React.SetStateAction<MessageItem[]>) => void;
  connected?: StoryItem[];
  setConnected: (value: React.SetStateAction<StoryItem[]>) => void;
  onNewMessage?: (msg?: NotifyMessage) => void;
  onRequestReload: () => void;
};

export function StoryDetails({
  item,
  messages,
  setMessages,
  connected,
  setConnected,
  onNewMessage,
  onRequestReload,
}: StoryDetailsProps) {
  const theme = useTheme();
  const [sortingOrder, setSortingOrder] = useState(
    item ? store.get(`SortingOrder:${item.PK}:${item.SK}`, false) : false
  );
  const captions = useAtomValue(captionsState);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const dragCounter = useRef(0);
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>();
  const projectName = useAtomValue(projectPickerState);
  const httpPost = useHttpPost();

  const getData = async () => {
    try {
      const response = await httpPost("stories", {
        connected: true,
        story: item.SK,
      });
      for (const m of response.data.connected) {
        const pp = m.st_pp;
        if (pp && typeof pp === "string") {
          m.st_pp = await u.parseJsonFromBinary(pp);
        }
      }
      setConnected(response.data.connected);
    } catch (err) {
      log.debug(err);
    } finally {
    }
  };

  const handleSort = () => {
    setSortingOrder(!sortingOrder);
    store(`SortingOrder:${item.PK}:${item.SK}`, !sortingOrder);
  };

  const getStoryCaption = (item: StoryItem) => storyCaption(item);

  const handleDelete = (other: StoryItem) => {
    if (!connected) {
      return;
    }

    const request = async () => {
      try {
        httpPost("label", {
          disconnect: {
            stories: [other.SK, item.SK],
          },
        });
      } catch (err) {}
    };
    request();

    let newValues = [];
    for (let v of connected) {
      if (v.SK !== other.SK) {
        newValues.push(v);
      }
    }
    setConnected(newValues);
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>, item: StoryItem) => {
    const url = u.getStoryUrl(item, projectName);

    if (e.metaKey) {
      e.stopPropagation();
      window.open(url, "_blank", "noreferrer");
    } else {
      window.location.href = url;
    }
  };

  const handleConnect = (value: string) => {
    const request = async (story: string) => {
      try {
        await httpPost("label", {
          connect: {
            stories: [story, item.SK],
          },
        });
        getData();
      } catch (err) {}
    };

    if (captions[projectName] && captions[projectName][value]) {
      request(captions[projectName][value]);
      return;
    }

    let story: string | undefined = value;
    try {
      const segments = new URL(value).pathname.split("/");
      const last = segments.pop() || segments.pop(); // Handle potential trailing slash
      story = last;
    } catch (e) {}

    if (story) {
      if (story.startsWith("STORY-")) {
        story = story.replace("STORY-", "");
      }
      request(story);
    }
  };

  const handleMessageSent = (msg?: NotifyMessage) => {
    setDroppedFiles(undefined);
    if (onNewMessage) {
      onNewMessage(msg);
    }
  };

  const handleMessageSending = () => {
    setDroppedFiles(undefined);
  };

  const handleReload = () => {
    if (onRequestReload) {
      onRequestReload();
    }
  };

  const countVisibleMessage = (messages: MessageItem[]) => {
    let count = 0;
    if (messages) {
      for (let m of messages) {
        if (!m.st_hide) {
          count += 1;
        }
      }
    }
    return count;
  };

  // const setMessagesOriginalOrder = (func: (messages: MessageItem[]) => MessageItem[]) => {
  //   setMessages((prev: MessageItem[]) => {
  //     const sorted = sortMessages(func(prev), !sortingOrder);
  //     return sorted;
  //   });
  // };
  const setMessagesOriginalOrder = (value: SetStateAction<MessageItem[]>) => {
    setMessages((prev: MessageItem[]) => {
      const unordered = typeof value === "function" ? value(prev) : value;
      const sorted = sortMessages(unordered, !sortingOrder);
      return sorted;
    });
  };

  const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragActive(false);

    getFilesFromDataTransfer(e.dataTransfer, setDroppedFiles);
  };

  const handleUploadedFileRemoved = useCallback(() => {
    setDroppedFiles(undefined);
  }, [setDroppedFiles]);

  return (
    <Stack
      direction="row"
      alignItems={"flex-start"}
      justifyContent="space-between"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        borderRadius: "8px",
      }}
    >
      {/* Drag overlay */}
      {dragActive && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(138, 180, 248, 0.08)"
                : "rgba(25, 103, 210, 0.08)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            animation: "fadeIn 0.2s ease",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              padding: 4,
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(31, 31, 31, 0.95)"
                  : "rgba(255, 255, 255, 0.95)",
              border: "2px dashed var(--accent-primary)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            }}
          >
            <Box
              sx={{
                fontSize: "48px",
                lineHeight: 1,
              }}
            >
              📎
            </Box>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                color: "var(--text-primary)",
              }}
            >
              Drop files here to attach
            </Typography>
          </Box>
        </Box>
      )}
      <Box width="100%" sx={{ p: 0, m: 0, pb: 1 }}>
        {connected && connected.length > 0 && (
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={0.5}
            alignItems="center"
            sx={{
              mt: 1.5,
              mb: 1.5,
              pl: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                color: "var(--text-secondary)",
                fontWeight: 500,
                mr: 0.5,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              related
            </Typography>
            {connected.map((item: StoryItem) => (
              <StyledTooltip
                key={item.SK}
                richContent
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "preventOverflow",
                        options: {
                          boundary: document.getElementById(
                            "main-scroll-container"
                          ),
                        },
                      },
                    ],
                  },
                }}
                title={<DarkStoryPreview item={item} fontSize={11} />}
              >
                <Chip
                  variant="outlined"
                  sx={{
                    maxWidth: "150px",
                    fontSize: "10px",
                    height: "18px",
                    backgroundColor: "transparent",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.12)"
                        : "rgba(0, 0, 0, 0.12)",
                    color: "var(--text-secondary)",
                    "& .MuiChip-label": {
                      px: 0.75,
                      py: 0,
                    },
                    "& .MuiChip-deleteIcon": {
                      fontSize: "12px",
                      color: "var(--text-tertiary)",
                      "&:hover": {
                        color: "var(--text-secondary)",
                      },
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                    },
                  }}
                  size="small"
                  label={getStoryCaption(item)}
                  onDelete={() => handleDelete(item)}
                  onClick={(e) => handleClick(e, item)}
                />
              </StyledTooltip>
            ))}
            <AutocompleteStoryInline onSelected={handleConnect}>
              <AddCircleIcon
                sx={{
                  fontSize: 14,
                  color: "var(--text-tertiary)",
                  display: "block",
                  "&:hover": {
                    color: "var(--text-secondary)",
                  },
                }}
              />
            </AutocompleteStoryInline>
          </Stack>
        )}
        {(!connected || connected.length === 0) && (
          <Box sx={{ mt: 1.5, mb: 1.5, pl: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{
                display: "inline-flex",
              }}
            >
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "var(--text-secondary)",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                related
              </Typography>
              <AutocompleteStoryInline onSelected={handleConnect}>
                <AddCircleIcon
                  sx={{
                    fontSize: 12,
                    color: "var(--text-tertiary)",
                    display: "block",
                    "&:hover": {
                      color: "var(--text-secondary)",
                    },
                  }}
                />
              </AutocompleteStoryInline>
            </Stack>
          </Box>
        )}

        <CommentSection
          onReload={handleReload}
          handleSort={handleSort}
          sortingOrder={sortingOrder}
          setMessages={setMessages}
          droppedFiles={droppedFiles}
          item={item}
          handleUploadedFileRemoved={handleUploadedFileRemoved}
          onMessageSent={handleMessageSent}
          onSending={handleMessageSending}
          bottomInputPresent={
            messages && countVisibleMessage(messages) > 15 ? true : false
          }
        >
          <>
            {messages && messages.length > 1 && (
              <Stack direction={"row"}>
                <Box sx={{ width: "50px" }}></Box>
                <MemoThread
                  messages={sortMessages(messages, sortingOrder)}
                  setMessages={setMessagesOriginalOrder}
                  allowReplay={false}
                  story={item}
                  background={undefined}
                  overflow={false}
                  expand={undefined}
                />
              </Stack>
            )}
          </>
        </CommentSection>
      </Box>
    </Stack>
  );
}

type StoryFastPreviewProps = {
  item?: StoryItem;
  fontSize?: number;
  sx?: any;
};

export function StoryFastPreview({
  item,
  fontSize,
  sx,
}: StoryFastPreviewProps) {
  const theme = useTheme();

  if (!item) {
    return null;
  }

  return (
    <Stack direction="column" sx={{ pb: 0.4 }}>
      <Stack direction="row" sx={{ mb: 1, mt: 1 }}>
        {item && item.SK4 && <StoryChip item={item} rowSelected={undefined} />}
        <TimelineChips item={item} />
      </Stack>
      {/* <StoryCell item={story} expand={false} hideControls={true} /> */}
      {item.st_cap && (
        <Box sx={{}}>
          <Chip
            size="small"
            disabled={false}
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? "rgba(138, 180, 248, 0.15)"
                  : "rgba(25, 103, 210, 0.1)",
              color: "var(--text-primary)",
              alignItems: "center",
              mb: 0.5,
              borderRadius: "12px",
              fontSize: "11px",
              height: "22px",
            }}
            label={item.st_cap}
          />
        </Box>
      )}
      <Box>
        <MessagePreview
          item={item}
          fontSize={fontSize || 11}
          readOnly
          sx={
            sx || {
              "&.MuiLink-root": {
                color: "var(--accent-primary)",
              },
            }
          }
          storyKey={undefined}
          overflow={undefined}
        />
      </Box>
    </Stack>
  );
}

export function DarkStoryPreview(props: any) {
  const theme = useTheme();

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  return (
    <Box onWheel={handleWheel}>
      <StyledTooltipContent isDark={theme.palette.mode === "dark"}>
        <StoryFastPreview {...props} />
      </StyledTooltipContent>
    </Box>
  );
}

const MemoThread = memo(Thread);
